import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * A white screen is the worst possible outcome for someone who has just been
 * handed a card by a stranger — it reads as broken, or worse, as dodgy.
 * If anything throws, she gets a calm, human page and a way out.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Intentionally console-only. No error-reporting vendor, because that would
    // mean shipping a third-party script to a page that promises it does not.
    console.error('Unhandled error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          maxWidth: '34rem',
          margin: '0 auto',
          padding: 'var(--section-y) var(--gutter)',
        }}
      >
        <h1 style={{ fontSize: 'var(--step-3)' }}>Well, that is embarrassing.</h1>
        <p style={{ marginTop: 'var(--space-s)', color: 'var(--text-secondary)' }}>
          Something broke at my end. Nothing you did, and nothing was sent anywhere.
          Reloading usually sorts it.
        </p>
        <p style={{ marginTop: 'var(--space-m)' }}>
          <a href="/">Back to the start</a>
        </p>
      </div>
    );
  }
}
